import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAccountContext } from './account-provider';
import { useInView } from 'react-intersection-observer';
import { getDdocsByPortalAddress } from '@/services/get-ddocs-by-portal-address';
import { RequestKey } from '@/utils/constants';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, UserCache } from '@/db/db';
import { addToUserDDocCache, deleteDocItem } from '@/db/queries';
import { IDdoc, MigrationStatus, PublishStatus } from '@/types';
import React from 'react';
import { getOrGenerateCommentKey } from '@/utils/migration-utils';
import { useGunStorageContext } from './gun-storage-provider';
import { getBaseUserCache } from '@/utils/file-utils';
import { isNullish } from '@/utils/gun-utils';
import { useRouter } from 'next/navigation';
import { deleteFileTransaction } from '@/utils/portal-utils';
import { waitForDeletedDdocDataById } from '@/services/get-ddoc-data-by-id';

interface IDdocListContext {
  ddocList: UserCache[] | IDdoc[];
  fetchNextListref: () => void;
  isFetchingDocs: boolean;
  isFetchingNextPage: boolean;
  isInitialized: boolean;
  uniqueDdocs: number;
  navigateToNextDoc: (currentDocId: string) => void;
  deductUniqueDdocsCount: () => void;
}

const DdocListContext = createContext<IDdocListContext>({
  ddocList: [],
  fetchNextListref: () => {},
  isFetchingDocs: false,
  isFetchingNextPage: false,
  isInitialized: false,
  uniqueDdocs: 0,
  navigateToNextDoc: () => {},
  deductUniqueDdocsCount: () => {},
});

export const useDdocListContext = () => useContext(DdocListContext);

export const DdocListProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { ref, inView } = useInView();
  const { walletAddress, portalAddress, identityInitialized } =
    useAccountContext();
  const { isGunInitialized } = useGunStorageContext();
  const [isFetchingDocs, setIsFetchingDocs] = useState<boolean>(true);
  const isFetchingRef = useRef(false);
  const [uniqueDdocs, setUniqueDdocs] = useState<number>(0);
  const [isDdocListInitialized, setIsDdocListInitialized] =
    useState<boolean>(false);
  const router = useRouter();

  const localIndexDBDdocs = useLiveQuery(async () => {
    return await db.ddocs.toArray();
  }, [] as IDdoc[]);

  const ddocList = useLiveQuery(
    async () => {
      if (!walletAddress || !isGunInitialized) {
        setIsFetchingDocs(false);
        return [];
      }

      const cachedDdocs = await db.userCache
        .where('address')
        .equals(walletAddress)
        .and(
          (doc) => isNullish(doc.markedForDeletion) || !doc.markedForDeletion
        )
        .toArray();

      const sortedDocs = cachedDdocs.toSorted(
        (a, b) => b.last_updated - a.last_updated
      );
      setIsFetchingDocs(false);
      return sortedDocs;
    },
    [walletAddress, portalAddress, isGunInitialized],
    [] as UserCache[]
  );

  const {
    fetchNextPage: fetchNextSession,
    isLoading: isDdocListFromIndexerLoading,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [RequestKey.GET_DDOCS, portalAddress],
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryFn: async ({ pageParam = 0 }) => {
      if (!portalAddress || !walletAddress) {
        return { ddocs: [], hasNext: false };
      }
      const response = await getDdocsByPortalAddress(
        portalAddress,
        pageParam,
        10
      );

      setUniqueDdocs((prev) => {
        if (prev === 0) return response.unique;
        return prev;
      });

      if (!response) {
        return { ddocs: [], hasNext: false };
      }

      const currentDdocs = response.ddocs || [];

      await Promise.all(
        currentDdocs.map(async (ddoc) => {
          const isDeleted = ddoc.isDeleted;
          if (isDeleted) await deleteDocItem(ddoc.ddocId, walletAddress);
          else {
            const cacheItem = getBaseUserCache({
              ddocId: ddoc.ddocId,
              createdAt:
                ddoc.createdBlockTimestamp ??
                ddoc.lastTransactionBlockTimestamp,
              last_updated: ddoc.lastTransactionBlockTimestamp,
              publishStatus: PublishStatus.Published,
              portalAddress: ddoc.portalAddress,
              title: null,
              linkKey: null,
              linkKeyNonce: ddoc.resolvedMetadata.nonce,
              encryptedTitle: ddoc.resolvedMetadata.title,
              onChainFileId: ddoc.fileId,
              archVersion: ddoc.resolvedMetadata.archVersion,
              ownerLock: ddoc.resolvedMetadata.ownerLock,
              portalLock: ddoc.resolvedMetadata.portalLock,
              needToDecryptTitle: true,
              commentKey: await getOrGenerateCommentKey(
                ddoc.resolvedMetadata.ownerLock
              ),
              migrationStatus:
                ddoc.resolvedMetadata.archVersion === 'v3'
                  ? null
                  : MigrationStatus.Pending,
              markedForDeletion: ddoc.isDeleted,
            });

            await addToUserDDocCache(walletAddress, cacheItem);
          }
        })
      );

      setIsDdocListInitialized(true);

      return response;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage?.hasNext ? allPages.length * 10 : undefined,
    enabled: !!portalAddress && !!walletAddress && isGunInitialized,
  });

  useEffect(() => {
    if (!inView || isFetchingRef.current || isFetchingNextPage) return;

    const controller = new AbortController();

    async function fetchNextPage() {
      if (isFetchingRef.current) return;

      isFetchingRef.current = true;
      try {
        await fetchNextSession();
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Error fetching next page:', error);
        }
      } finally {
        isFetchingRef.current = false;
      }
    }

    fetchNextPage();

    return () => {
      controller.abort();
      isFetchingRef.current = false;
    };
  }, [inView, fetchNextSession, isFetchingNextPage]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    async function migrateLocalDocsToUserCache() {
      if (!walletAddress || !localIndexDBDdocs?.length || !isMounted) return;

      try {
        await Promise.all(
          localIndexDBDdocs
            .filter((doc) => !doc.onChainFileId)
            .map(async (doc) => {
              if (!isMounted) return;

              const existingDoc = await db.userCache
                .where(['address', 'ddocId'])
                .equals([walletAddress, doc.ddocId])
                .and(
                  (doc) =>
                    isNullish(doc.markedForDeletion) || !doc.markedForDeletion
                )
                .first();

              if (!existingDoc && isMounted) {
                const cacheItem: UserCache = getBaseUserCache({
                  address: walletAddress,
                  ddocId: doc.ddocId,
                  createdAt: doc.createdAt,
                  last_updated: doc.last_updated,
                  publishStatus: doc.publishStatus,
                  portalAddress: portalAddress || '',
                  title: doc.title,
                  commentKey: doc.commentKey,
                });

                await addToUserDDocCache(walletAddress, cacheItem);
              }
            })
        );
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Error migrating docs:', error);
        }
      }
    }

    migrateLocalDocsToUserCache();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [walletAddress, portalAddress]);

  const actualDocList = useMemo(() => {
    return walletAddress ? (ddocList ?? []) : (localIndexDBDdocs ?? []);
  }, [walletAddress, ddocList, localIndexDBDdocs]);

  const navigateToNextDoc = (currentDocId: string) => {
    const nextDoc = actualDocList.find((doc) => doc.ddocId !== currentDocId);
    if (nextDoc) {
      router.replace(`/document/${nextDoc.ddocId}`);
    } else {
      router.replace('/document/create');
    }
  };

  useEffect(() => {
    async function deletePendingDocs() {
      if (!walletAddress || !identityInitialized) return;
      const pendingDocs = await db.userCache
        .where('address')
        .equals(walletAddress)
        .and((doc) => doc.markedForDeletion)
        .toArray();

      for (const doc of pendingDocs) {
        try {
          await deleteFileTransaction(doc, walletAddress);
          // check if the doc is deleted from the indexer, then remove from cache
          const ddocDataFromIndexer = await waitForDeletedDdocDataById(
            doc.ddocId,
            doc.portalAddress as string
          );
          if (ddocDataFromIndexer?.isDeleted) {
            await deleteDocItem(doc.ddocId, walletAddress);
          }
        } catch (error) {
          console.error('Error deleting pending doc:', error);
        }
      }
    }

    deletePendingDocs();
  }, [walletAddress, identityInitialized]);

  const deductUniqueDdocsCount = () => {
    setUniqueDdocs((prev) => {
      if (prev === 0) return 0;
      return prev - 1;
    });
  };

  useEffect(() => {
    return () => {
      setUniqueDdocs(0);
    };
  }, [walletAddress]);

  return (
    <DdocListContext.Provider
      value={{
        ddocList: actualDocList,
        fetchNextListref: ref,
        isFetchingDocs: isFetchingDocs || isDdocListFromIndexerLoading,
        isFetchingNextPage,
        isInitialized: isDdocListInitialized,
        uniqueDdocs,
        navigateToNextDoc,
        deductUniqueDdocsCount,
      }}
    >
      {children}
    </DdocListContext.Provider>
  );
};
